/**
 * IndexedDB persistence using idb-keyval.
 * Stores identity keys, display name, and device preferences.
 * Database: mrmr-feather
 */

import { createStore, get, set, del, clear } from 'idb-keyval'
import type { DevicePreferences, StoredIdentity } from '../types'

const store = createStore('mrmr-feather', 'preferences')

// Keys used in the store
const KEY_DISPLAY_NAME = 'displayName'
const KEY_DEVICE_PREFS = 'devicePreferences'
const KEY_IDENTITY = 'identity'

// ============================================================
// Display Name
// ============================================================

export async function saveDisplayName(name: string): Promise<void> {
  await set(KEY_DISPLAY_NAME, name, store)
}

export async function loadDisplayName(): Promise<string | null> {
  const name = await get<string>(KEY_DISPLAY_NAME, store)
  return name ?? null
}

// ============================================================
// Device Preferences
// ============================================================

export async function saveDevicePreferences(
  prefs: DevicePreferences,
): Promise<void> {
  await set(KEY_DEVICE_PREFS, prefs, store)
}

export async function loadDevicePreferences(): Promise<DevicePreferences | null> {
  const prefs = await get<DevicePreferences>(KEY_DEVICE_PREFS, store)
  return prefs ?? null
}

// ============================================================
// Identity Keys
// ============================================================

export async function saveIdentityKeys(
  identity: StoredIdentity,
): Promise<void> {
  await set(KEY_IDENTITY, identity, store)
}

export async function loadIdentityKeys(): Promise<StoredIdentity | null> {
  const identity = await get<StoredIdentity>(KEY_IDENTITY, store)
  return identity ?? null
}

// ============================================================
// Persisted State (load all at once)
// ============================================================

export interface PersistedState {
  displayName: string | null
  devicePreferences: DevicePreferences | null
  identity: StoredIdentity | null
}

export async function loadPersistedState(): Promise<PersistedState> {
  const [displayName, devicePreferences, identity] = await Promise.all([
    loadDisplayName(),
    loadDevicePreferences(),
    loadIdentityKeys(),
  ])
  return { displayName, devicePreferences, identity }
}

// ============================================================
// Clear
// ============================================================

export async function clearAll(): Promise<void> {
  await clear(store)
}

// ============================================================
// Known Peers (TOFU)
// ============================================================

const KNOWN_PEER_PREFIX = 'known-peer:'

export interface KnownPeer {
  participantId: string
  identityPublicKey: JsonWebKey
  kxPublicKey: JsonWebKey
  firstSeen: number
  lastSeen: number
  fingerprint: string
  trusted: boolean
}

export async function saveKnownPeer(peer: KnownPeer): Promise<void> {
  await set(KNOWN_PEER_PREFIX + peer.participantId, peer, store)
}

export async function loadKnownPeer(
  participantId: string,
): Promise<KnownPeer | null> {
  const peer = await get<KnownPeer>(
    KNOWN_PEER_PREFIX + participantId,
    store,
  )
  return peer ?? null
}

export async function deleteKnownPeer(
  participantId: string,
): Promise<void> {
  await del(KNOWN_PEER_PREFIX + participantId, store)
}
